import React, { useMemo } from "react";
import Joyride, { STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { markBulkOptimizeTourCompleted } from "../utils/bulkOptimizeTourStorage";

export { hasCompletedBulkOptimizeTour, markBulkOptimizeTourCompleted } from "../utils/bulkOptimizeTourStorage";

const tourText = {
  title: { fontSize: "1.2rem", fontWeight: 700, color: "#111827", marginBottom: "0.5rem", lineHeight: 1.35 },
  body: { fontSize: "1rem", lineHeight: 1.55, color: "#374151", marginBottom: 0 },
};

/**
 * Guided tour for SEO Bulk Optimize (templates, table, run). Alt template step is omitted for category-only template mode.
 */
export default function BulkOptimizeGuidedTour({ run, onClose, onExit, includeAltTemplateStep = true }) {
  const { t, i18n } = useTranslation();

  const steps = useMemo(() => {
    const all = [
      {
        target: "body",
        content: (
          <div style={{ maxWidth: 480 }}>
            <p className="mb-2 fw-bold" style={{ ...tourText.title, fontSize: "1.3rem" }}>
              {t("bulkOptimizeTour.welcomeTitle")}
            </p>
            <p style={tourText.body}>{t("bulkOptimizeTour.welcomeBody")}</p>
          </div>
        ),
        placement: "center",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-resource-toggle",
        content: (
          <div style={{ maxWidth: 420 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.resourceTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.resourceBody")}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-queue",
        content: (
          <div style={{ maxWidth: 420 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.queueTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.queueBody")}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-header",
        content: (
          <div style={{ maxWidth: 460 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templateOverviewTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templateOverviewBody")}</p>
          </div>
        ),
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-fields-pills",
        content: (
          <div style={{ maxWidth: 460 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templateFieldsTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templateFieldsBody")}</p>
          </div>
        ),
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-title-block",
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templatePageTitleTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templatePageTitleBody")}</p>
          </div>
        ),
        placement: "auto",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-meta-block",
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templateMetaTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templateMetaBody")}</p>
          </div>
        ),
        placement: "auto",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-alt-block",
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templateAltTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templateAltBody")}</p>
          </div>
        ),
        placement: "auto",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-template-save-bar",
        content: (
          <div style={{ maxWidth: 460 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.templateSaveTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.templateSaveBody")}</p>
          </div>
        ),
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#bulk-tour-table",
        content: (
          <div style={{ maxWidth: 460 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.tableTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.tableBody")}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: "#run-bulk-update-section",
        content: (
          <div style={{ maxWidth: 440 }}>
            <p style={tourText.title}>{t("bulkOptimizeTour.runTitle")}</p>
            <p style={tourText.body}>{t("bulkOptimizeTour.runBody")}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: "body",
        content: (
          <div style={{ maxWidth: 480 }}>
            <p className="mb-2 fw-bold" style={{ ...tourText.title, fontSize: "1.3rem" }}>
              {t("bulkOptimizeTour.doneTitle")}
            </p>
            <p style={tourText.body}>{t("bulkOptimizeTour.doneBody")}</p>
          </div>
        ),
        placement: "center",
        disableBeacon: true,
      },
    ];
    if (!includeAltTemplateStep) {
      return all.filter((s) => s.target !== "#bulk-tour-template-alt-block");
    }
    return all;
  }, [t, i18n.language, includeAltTemplateStep]);

  const handleCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      markBulkOptimizeTourCompleted();
      const reason = status === STATUS.FINISHED ? "finished" : "skipped";
      onClose();
      window.requestAnimationFrame(() => {
        if (onExit) onExit(reason);
      });
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      scrollOffset={100}
      disableScrolling={false}
      spotlightPadding={8}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 11000,
          primaryColor: "#0d6efd",
          textColor: "#1f2937",
          width: undefined,
        },
        tooltip: {
          borderRadius: 14,
          fontSize: 16,
          padding: "18px 22px",
          minWidth: 360,
          maxWidth: "min(520px, calc(100vw - 32px))",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
        },
        tooltipContainer: {
          maxWidth: "min(520px, calc(100vw - 32px))",
        },
        tooltipContent: {
          padding: "2px 0 6px",
          fontSize: 16,
          lineHeight: 1.5,
        },
        tooltipFooter: {
          marginTop: 14,
        },
        buttonNext: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 15,
          padding: "10px 18px",
        },
        buttonBack: { color: "#6b7280", fontSize: 15 },
        buttonSkip: { color: "#6b7280", fontSize: 14 },
        buttonClose: { height: 14, width: 14, padding: 8 },
      }}
      locale={{
        back: t("guidedTour.btnBack"),
        close: t("guidedTour.btnClose"),
        last: t("guidedTour.btnDone"),
        next: t("guidedTour.btnNext"),
        skip: t("guidedTour.btnSkip"),
      }}
    />
  );
}
